import { useContext } from 'react'

import { WindowsCreateOrEditContext } from '@pages/WindowsCreateOrEdit'
import { FormSectionType, WindowConfigType } from '@pages/WindowsCreateOrEdit/types'

import { WindowConfigContainerType } from '@types'

import { useHandlers } from './useHandlers'

export const useWindowConfig = () => {
  const { windowById, isLoadingWindowById } = useContext(WindowsCreateOrEditContext)

  const sections = windowById?.sections || []
  const configs = windowById?.configs || []

  const filterSections = (
    allSections: FormSectionType[],
    usedSections: WindowConfigType[]
  ): FormSectionType[] => {
    if (!allSections || allSections.length === 0) return []
    if (!usedSections || usedSections.length === 0) return allSections

    const configSectionIds = new Set(configs.map(config => String(config.sectionId)))

    return sections.filter(section => !configSectionIds.has(String(section.id)))
  }

  const unusedSections = filterSections(sections, configs)

  const extractLines = (containers: WinadowConfigContainerType[]) => {
    if (!containers || containers.length === 0) {
      return []
    }

    const firstContainer = containers[0]

    if (firstContainer.tabs && firstContainer.tabs.length > 0) {
      return firstContainer?.tabs[0]?.lines || []
    }

    return []
  }

  const handleTransformConfigsToPOST = (containers: WindowConfigContainerType) => {
    const lines = extractLines(containers)

    return (
      lines?.flatMap((line, index): WindowConfigType<string | number>[] => {
        return line.elements.map(el => ({
          sectionId: el.id,
          sectionTitle: el.title,
          line: index,
          column: line.elements.length === 1 ? 0 : el?.order,
          size: el.size,
        }))
      }) || []
    )
  }

  const windowId = windowById?.id || undefined

  const initialContainers: WindowConfigContainerType[] = [
    {
      id: 0,
      order: 0,
      title: 'Section',
      windowId,
      tabs: [
        {
          id: 0,
          order: 0,
          title: 'Section Lines',
          lines: [
            {
              id: 0,
              order: 0,
              elements: [],
            },
            {
              id: 1,
              order: 0,
              elements: [],
            },
          ],
        },
      ],
    },
  ]

  const transformConfigsToContainers = (
    configs: any[],
    initialContainers: WindowConfigContainerType[]
  ): WindowConfigContainerType[] => {
    // Создаем глубокую копию исходного массива, чтобы не мутировать оригинал
    const containers = JSON.parse(JSON.stringify(initialContainers))

    // Очищаем все элементы в lines, так как мы будем заполнять их заново
    containers.forEach(container => {
      container.tabs.forEach(tab => {
        tab.lines.forEach(line => {
          line.elements = []
        })
      })
    })

    // Заполняем элементы согласно configs
    configs.forEach(config => {
      const { line, ...elementData } = config

      // Находим соответствующую линию в контейнерах
      containers.forEach(container => {
        container.tabs.forEach(tab => {
          const targetLine = tab.lines.find(l => l.id === line)
          if (targetLine) {
            targetLine.elements.push({
              ...elementData,
              id: elementData?.sectionId || elementData?.id?.toString(),
              order: elementData.column,
              size: elementData.size,
              data: null,
              title: elementData?.sectionTitle,
            })
          }
        })
      })
    })

    return containers
  }

  const { data, handlers, state } = useHandlers()

  return {
    data: {
      ...data,
      unusedSections,
      isLoadingWindowById,
      windowId,
      windowById,
      configs,
      initialContainers,
    },
    state: { ...state },
    handlers: { ...handlers, handleTransformConfigsToPOST, transformConfigsToContainers },
  }
}
